// The Kinds tab in the help panel. It's generated from kinds.ts and drawn from the same atlas as
// the field, so it can't drift from what actually draws. Clicking a kind inserts it in the code.
import type { EditorView } from '@codemirror/view';
import { insertSymbol } from './editor.ts';
import { kindGroups, kindSpec, laserKinds } from './kinds.ts';
import type { Renderer } from './renderer.ts';

/** Thumbnails fit a square this many CSS pixels wide. */
const BOX = 40;

export class KindsPanel {
  private el: HTMLElement;
  private renderer: Renderer;
  private editor: EditorView;
  private builtFor = -1;

  constructor(el: HTMLElement, renderer: Renderer, editor: EditorView) {
    this.el = el;
    this.renderer = renderer;
    this.editor = editor;
  }

  /** Builds the list, or rebuilds it when the sprites have changed since. */
  refresh(): void {
    if (this.builtFor === this.renderer.atlasVersion) return;
    this.builtFor = this.renderer.atlasVersion;
    const parts: HTMLElement[] = [];
    for (const group of kindGroups()) {
      const heading = document.createElement('h3');
      heading.textContent = group.label;
      const grid = document.createElement('div');
      grid.className = 'kind-grid';
      for (const name of group.names) grid.append(this.tile(name));
      parts.push(heading, grid);
    }
    this.el.replaceChildren(...parts);
  }

  private tile(name: string): HTMLButtonElement {
    const button = document.createElement('button');
    button.className = 'kind';
    button.title = laserKinds[name] ? `Insert '${name}` : `Insert '${name}. Hit radius ${kindSpec(name).hit}.`;

    // Small sprites scale up by whole steps so they stay crisp; big ones shrink to fit.
    const thumb = this.renderer.thumbnail(name);
    const size = Math.max(thumb.width, thumb.height);
    const zoom = size <= BOX ? Math.max(1, Math.floor(BOX / size)) : BOX / size;
    thumb.style.width = `${thumb.width * zoom}px`;
    thumb.style.height = `${thumb.height * zoom}px`;
    thumb.style.imageRendering = zoom >= 1 ? 'pixelated' : 'auto';
    const box = document.createElement('span');
    box.className = 'thumb';
    box.append(thumb);

    const label = document.createElement('code');
    label.textContent = `'${name}`;
    button.append(box, label);
    button.addEventListener('click', () => insertSymbol(this.editor, `'${name}`));
    return button;
  }
}
